import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

interface SidebarContextType {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const STORAGE_KEY = 'rollops-admin-sidebar-open';

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Persist open state to avoid reflow/jitter on route changes (provider remounts per page)
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return window.innerWidth >= 768; // default: open on desktop, closed on mobile
  });

  // Track if user has manually toggled the sidebar (use ref to avoid stale closures)
  const userToggledRef = useRef(false);
  // Track if a transition is in progress to prevent resize handler interference
  const isTransitioningRef = useRef(false);
  // Track timeout IDs for cleanup to prevent memory leaks
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track resize timeout ID for proper cleanup
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update sidebar state on window resize
  // Only auto-toggle if user hasn't manually changed it
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      // Debounce resize events - use longer timeout than CSS transition (300ms)
      // Clear any existing timeout before setting a new one
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        // Don't interfere if user is manually toggling or transition is in progress
        if (isTransitioningRef.current) {
          return;
        }

        const isDesktop = window.innerWidth >= 768;
        // Only auto-toggle if user hasn't manually toggled
        // On mobile, always close sidebar
        // On desktop, only auto-open if user hasn't manually closed it
        if (!isDesktop) {
          setIsOpen(false);
          // On mobile, always close on resize - reset ref to allow future auto-adjustment
          // This is safe because mobile always closes regardless of user preference
          userToggledRef.current = false;
        } else if (!userToggledRef.current) {
          // On desktop, default to open if user hasn't manually toggled
          setIsOpen(true);
          // Don't reset userToggledRef here - it's already false, and we want to preserve
          // the state that allows auto-adjustment. Resetting it would be redundant and
          // could cause issues if the user manually toggles after this resize.
        }
        // Note: If userToggledRef.current is true (user manually toggled), we don't
        // auto-adjust, preserving the user's manual preference across resizes
        resizeTimeoutRef.current = null; // Clear ref after timeout executes
      }, 350); // Longer than CSS transition (300ms) to prevent interference
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      // Properly cleanup resize timeout using ref
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
    };
  }, []); // Empty deps - use refs instead

  // Helper function to clear transition timeout
  const clearTransitionTimeout = () => {
    if (transitionTimeoutRef.current !== null) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  };

  const persist = (next: boolean) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    }
  };

  const toggle = () => {
    userToggledRef.current = true;
    isTransitioningRef.current = true;
    setIsOpen(prev => {
      const next = !prev;
      persist(next);
      return next;
    });
    clearTransitionTimeout();
    transitionTimeoutRef.current = setTimeout(() => {
      isTransitioningRef.current = false;
      transitionTimeoutRef.current = null;
    }, 400);
  };
  const open = () => {
    userToggledRef.current = true;
    isTransitioningRef.current = true;
    setIsOpen(true);
    persist(true);
    clearTransitionTimeout();
    transitionTimeoutRef.current = setTimeout(() => {
      isTransitioningRef.current = false;
      transitionTimeoutRef.current = null;
    }, 400);
  };
  const close = () => {
    userToggledRef.current = true;
    isTransitioningRef.current = true;
    setIsOpen(false);
    persist(false);
    clearTransitionTimeout();
    transitionTimeoutRef.current = setTimeout(() => {
      isTransitioningRef.current = false;
      transitionTimeoutRef.current = null;
    }, 400);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearTransitionTimeout();
    };
  }, []);

  return (
    <SidebarContext.Provider value={{ isOpen, toggle, open, close }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
